import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const RATINGS = [
  { id: "great", emoji: "🌟", label: "Magique" },
  { id: "good",  emoji: "😊", label: "Chouette" },
  { id: "mixed", emoji: "🤔", label: "Mitigé" },
  { id: "tough", emoji: "😅", label: "Compliqué" },
];

export default function WrapUp() {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [overall, setOverall] = useState(null);
  const [favorite, setFavorite] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getJourney(journeyId)
      .then(setJourney)
      .catch((e) => {
        console.error(e);
        toast.error("Bilan introuvable. Recommencez une matinée.");
        navigate("/");
      });
  }, [journeyId, navigate]);

  const submit = async () => {
    if (!overall) {
      toast.error("Choisissez d'abord une appréciation globale.");
      return;
    }
    setSubmitting(true);
    try {
      await api.submitWrapUp(journeyId, {
        overall_rating: overall,
        favorite_mission_id: favorite ?? null,
        note: note.trim() || null,
      });
      try { localStorage.removeItem("mlou_journey_id"); } catch (_) {}
      toast.success("✨ Merci ! Vos retours aident à personnaliser la suite.");
      navigate("/journey-completed");
    } catch (err) {
      console.error(err);
      toast.error("Impossible d'enregistrer le bilan.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!journey) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md items-center justify-center px-6">
        <p className="text-[#5b4b6b]">Chargement du bilan…</p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-10">
      <header className="mb-6 text-center">
        <div className="text-5xl">🌟</div>
        <h1 className="mt-3 font-[Fredoka] text-3xl font-semibold text-[#3b2a4a]">
          Bilan de la matinée
        </h1>
        <p className="mt-2 text-sm text-[#5b4b6b]">
          Neo et Lyria veulent savoir comment s'est passée la matinée.
        </p>
      </header>

      <section className="mb-6 rounded-3xl bg-white/85 p-5 shadow-md backdrop-blur">
        <p className="mb-3 text-sm font-medium text-[#3b2a4a]">
          Comment qualifierez-vous la matinée ?
        </p>
        <div className="grid grid-cols-2 gap-2">
          {RATINGS.map((r) => {
            const active = overall === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setOverall(r.id)}
                className={[
                  "flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium shadow-sm transition",
                  active ? "bg-[#ff8a65] text-white" : "bg-[#fdf3e8] text-[#3b2a4a] hover:bg-[#fbe4cd]",
                ].join(" ")}
              >
                <span className="text-xl" aria-hidden>{r.emoji}</span>
                {r.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="mb-6 rounded-3xl bg-white/85 p-5 shadow-md backdrop-blur">
        <p className="mb-3 text-sm font-medium text-[#3b2a4a]">
          Mission préférée ?
          <span className="ml-1 text-xs font-normal text-[#7a6b8a]">(facultatif)</span>
        </p>
        <ul className="space-y-2">
          {journey.missions.map((m) => {
            const active = favorite === m.id;
            return (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => setFavorite(active ? null : m.id)}
                  className={[
                    "flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm shadow-sm transition",
                    active ? "bg-[#ff8a65] text-white" : "bg-[#fdf3e8] text-[#3b2a4a] hover:bg-[#fbe4cd]",
                  ].join(" ")}
                >
                  <span>{m.title}</span>
                  <span className="text-xs opacity-80">{m.duration_minutes} min</span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="mb-6 rounded-3xl bg-white/85 p-5 shadow-md backdrop-blur">
        <label htmlFor="note" className="mb-2 block text-sm font-medium text-[#3b2a4a]">
          Une note pour Neo & Lyria ?
          <span className="ml-1 text-xs font-normal text-[#7a6b8a]">(facultatif)</span>
        </label>
        <textarea
          id="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="Ce qui a marché, ce qu'on pourrait changer…"
          className="w-full resize-none rounded-2xl border-none bg-[#fdf3e8] p-3 text-sm text-[#3b2a4a] shadow-inner focus:outline-none focus:ring-2 focus:ring-[#ff8a65]/40"
        />
      </section>

      <Button
        onClick={submit}
        disabled={submitting}
        size="lg"
        className="rounded-full bg-[#ff8a65] py-6 text-base font-semibold text-white shadow-lg hover:bg-[#ff7043] disabled:opacity-60"
      >
        {submitting ? "Enregistrement…" : "Envoyer le bilan ✨"}
      </Button>

      <Link to="/" className="mt-4 text-center text-xs text-[#7a6b8a]">
        Sauter pour cette fois
      </Link>
    </main>
  );
}
